import moment from 'moment-timezone';

import { combineDateAndTime } from './date';

const DAY_OF_WEEK_TO_INDEX = {
  MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6, SUN: 7,
};

const DAY_SHORT_LABEL = {
  MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun',
};

const RecurrenceType = Object.freeze({
  WEEKLY: 'weekly',
});

const nextWeeklyOccurrence = ({ now, recurrence }) => {
  const {
    interval,
    byDay,
    sendTime,
    timeZone,
    anchorDate,
    endDate,
  } = recurrence;

  const [hour, minute] = sendTime.split(':').map(Number);
  const targetDows = byDay.map((d) => DAY_OF_WEEK_TO_INDEX[d]);
  const anchorWeekStart = moment.tz(anchorDate, timeZone).startOf('isoWeek');

  const horizonDays = (interval * 7) + 7;
  for (let dayOffset = 0; dayOffset < horizonDays; dayOffset += 1) {
    const candidate = moment.tz(now, timeZone)
      .add(dayOffset, 'days')
      .hour(hour)
      .minute(minute)
      .second(0)
      .millisecond(0);

    const isChosenWeekday = targetDows.includes(candidate.isoWeekday());
    const candidateWeekStart = candidate.clone().startOf('isoWeek');
    const weeksSinceAnchor = candidateWeekStart.diff(anchorWeekStart, 'weeks');
    const isOnWeek = weeksSinceAnchor >= 0 && weeksSinceAnchor % interval === 0;
    const candidateUtc = candidate.utc().valueOf();
    const isFuture = candidateUtc > now;

    if (isChosenWeekday && isOnWeek && isFuture) {
      return (endDate && candidateUtc > endDate) ? null : candidate.utc();
    }
  }
  return null;
};

const nextOccurrence = ({ now, recurrence }) => {
  switch (recurrence.type) {
    case RecurrenceType.WEEKLY:
      return nextWeeklyOccurrence({ now, recurrence });
    default:
      return null;
  }
};

const buildRecurrenceRule = ({
  isRecurring,
  sendDate,
  sendTime,
  interval,
  byDay,
  endDate,
  timeZone,
}) => {
  if (!isRecurring || byDay.length === 0 || !sendDate || !sendTime) return null;

  const localDateTime = combineDateAndTime(sendDate.clone(), sendTime);

  const targetDows = byDay.map((d) => DAY_OF_WEEK_TO_INDEX[d]);
  const anchorMoment = localDateTime.clone();
  for (let i = 0; i < 7; i += 1) {
    if (targetDows.includes(anchorMoment.isoWeekday())) break;
    anchorMoment.add(1, 'day');
  }

  const endDateMs = endDate
    ? moment.tz(endDate, timeZone).endOf('day').utc().valueOf()
    : null;

  if (endDateMs && endDateMs < anchorMoment.utc().valueOf()) return null;

  return {
    type: RecurrenceType.WEEKLY,
    interval,
    byDay,
    sendTime: localDateTime.format('HH:mm'),
    timeZone,
    anchorDate: anchorMoment.utc().valueOf(),
    ...(endDateMs && { endDate: endDateMs }),
  };
};

const formatRecurrenceSummary = (recurrence) => {
  if (!recurrence || !recurrence.byDay || recurrence.byDay.length === 0) {
    return '';
  }
  const { interval, byDay } = recurrence;
  const cadence = interval === 1 ? 'Every week' : `Every ${interval} weeks`;
  const days = byDay.map((d) => DAY_SHORT_LABEL[d]).join(', ');
  return `${cadence} on ${days}`;
};

export {
  RecurrenceType,
  nextOccurrence,
  formatRecurrenceSummary,
  buildRecurrenceRule,
};
